import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, Car, CircleDot, Droplets, TrendingUp, Map, Calculator, AlertTriangle, Settings, Zap } from 'lucide-react';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import SectionTitle from '../../ui/SectionTitle';
import { fadeInUp, stagger } from '../../../lib/animations';

const tools = [
  {
    slug: 'validar-pneu-dot',
    icon: CircleDot,
    title: 'Validador de Pneu (DOT)',
    description:
      'Descubra a data de fabricação e a validade do seu pneu pelo código DOT. Saiba se está na hora de trocar antes de colocar a família em risco.',
    badge: 'Segurança',
    href: '/ferramentas/validar-pneu-dot',
  },
  {
    slug: 'simulador-pontos-cnh',
    icon: Car,
    title: 'Simulador de Pontos CNH',
    description:
      'Simule sua pontuação na CNH na janela deslizante de 12 meses e descubra o risco de suspensão antes que seja tarde.',
    badge: 'Legislação',
    href: '/ferramentas/simulador-pontos-cnh',
  },
  {
    slug: 'calculadora-flex',
    icon: Droplets,
    title: 'Calculadora Flex',
    description:
      'Informe os preços do posto e calcule se compensa abastecer com álcool ou gasolina hoje — com ou sem o consumo real do seu carro.',
    badge: 'Economia',
    href: '/ferramentas/calculadora-flex',
  },
  {
    slug: 'calculadora-lucro-uber',
    icon: TrendingUp,
    title: 'Calculadora de Lucro Uber / 99',
    description:
      'Descubra quanto você realmente ganhou no dia, descontando combustível, depreciação do veículo e gastos extras.',
    badge: 'Motorista App',
    href: '/ferramentas/calculadora-lucro-uber',
  },
  {
    slug: 'calculadora-viagem',
    icon: Map,
    title: 'Calculadora de Viagem & Racha Conta',
    description:
      'Trace a rota, calcule a distância exata, gasto com combustível/pedágio e o valor do PIX por passageiro.',
    badge: 'Viagem',
    href: '/ferramentas/calculadora-viagem',
  },
  {
    slug: 'calculadora-custo-km',
    icon: Calculator,
    title: 'Calculadora Custo Real por KM (TCO)',
    description:
      'Descubra quanto seu carro realmente custa. Inclua IPVA, manutenções, peças invisíveis e tenha o custo exato.',
    badge: 'Finanças',
    href: '/ferramentas/calculadora-custo-km',
  },
  {
    slug: 'simulador-maus-habitos',
    icon: AlertTriangle,
    title: 'Simulador de Maus Hábitos',
    description:
      'Confesse seus pecados no trânsito e veja quanto dinheiro você joga fora com pneu murcho e aceleradas.',
    badge: 'Economia',
    href: '/ferramentas/simulador-maus-habitos',
  },
  {
    slug: 'simulador-vida-util',
    icon: Settings,
    title: 'Simulador de Vida Útil e Trocas',
    description:
      'Você já deveria ter trocado a correia? Insira a KM do carro e saiba quais peças já venceram.',
    badge: 'Manutenção',
    href: '/ferramentas/simulador-vida-util',
  },
  {
    slug: 'calculadora-recarga-ev',
    icon: Zap,
    title: 'Calculadora de Recarga de Elétricos (EV)',
    description:
      'Vai demorar 1 hora ou 40 horas? Calcule o tempo exato e o gasto de energia (conta de luz) para recarregar.',
    badge: 'Inovação',
    href: '/ferramentas/calculadora-recarga-ev',
  },
] as const;

/** Página-índice de ferramentas automotivas (/ferramentas) */
const FerramentasIndex: React.FC = () => (
  <>
    <Helmet>
      <title>Ferramentas Automotivas Gratuitas – Gas2</title>
      <meta
        name="description"
        content="Calculadoras e simuladores automotivos gratuitos: validade do pneu pelo código DOT, pontos da CNH, álcool vs gasolina e lucro real Uber/99. Use agora, sem cadastro."
      />
      <link rel="canonical" href="https://gas2.app/ferramentas" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Gas2" />
      <meta property="og:url" content="https://gas2.app/ferramentas" />
      <meta property="og:title" content="Ferramentas Automotivas Gratuitas – Gas2" />
      <meta
        property="og:description"
        content="Calculadoras e simuladores automotivos gratuitos: validade do pneu pelo código DOT, pontos da CNH, álcool vs gasolina e lucro real Uber/99."
      />
      <meta property="og:image" content="https://gas2.app/logo512.png" />
      <meta property="og:locale" content="pt_BR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Ferramentas Automotivas Gratuitas – Gas2" />
      <meta
        name="twitter:description"
        content="Calculadoras e simuladores automotivos gratuitos: validade do pneu DOT, pontos da CNH, álcool vs gasolina e lucro real Uber/99."
      />
      <meta name="twitter:image" content="https://gas2.app/logo512.png" />
    </Helmet>

    <Navbar solid />

    <main className="min-h-screen bg-slate-950 pt-24 pb-20">
      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-16">
        <SectionTitle
          eyebrow="Ferramentas Gas2"
          title="Calculadoras Automotivas"
          subtitle="Ferramentas gratuitas para calcular consumo, validade de pneus, pontos da CNH e rentabilidade como motorista de app. Sem cadastro, sem anúncio."
          align="center"
          dark
        />
      </section>

      {/* Cards */}
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 grid gap-6 sm:grid-cols-2"
      >
        {tools.map(tool => {
          const Icon = tool.icon;
          return (
            <motion.a
              key={tool.slug}
              href={tool.href}
              variants={fadeInUp}
              className="group flex flex-col gap-5 rounded-2xl bg-slate-900 border border-slate-800 hover:border-amber-400/40 p-7 transition-colors duration-300"
            >
              <div className="flex items-start justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-400/10">
                  <Icon className="h-6 w-6 text-amber-400" strokeWidth={1.8} />
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-800 text-slate-400 border border-slate-700">
                  {tool.badge}
                </span>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-xl font-bold text-white group-hover:text-amber-400 transition-colors duration-200 leading-snug">
                  {tool.title}
                </h2>
                <p className="mt-2 text-slate-400 text-sm leading-relaxed">
                  {tool.description}
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-amber-400 text-sm font-semibold">
                Usar ferramenta
                <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </div>
            </motion.a>
          );
        })}
      </motion.div>
    </main>

    <Footer />
  </>
);

export default FerramentasIndex;

